import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  HEADER_HEIGHT,
  SEPARATOR_HEIGHT,
  MEDIA_BREAKPOINTS,
} from '@/constants/layout';

const HERO_IMAGE_ALT = 'די ג׳יי אליאור מור - תמונת פתיחה';

interface SectionBaseProps {
  $useCssBg: boolean;
  $backgroundImageUrl?: string;
}

const SectionBase = styled.section<SectionBaseProps>`
  position: relative;
  overflow: hidden;
  contain: layout paint;
  isolation: isolate;

  height: calc(100svh - ${SEPARATOR_HEIGHT.smallScreen * 2}px);
  min-height: calc(100svh - ${SEPARATOR_HEIGHT.smallScreen * 2}px);
  max-height: calc(100svh - ${SEPARATOR_HEIGHT.smallScreen * 2}px);

  background-color: #000;

  /* ✅ רק לסקשנים שהם לא Hero - משתמשים ב־CSS background */
  background-image: ${({ $useCssBg, $backgroundImageUrl }) =>
    $useCssBg && $backgroundImageUrl ? `url(${$backgroundImageUrl})` : 'none'};
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    height: calc(
      100svh - ${HEADER_HEIGHT + SEPARATOR_HEIGHT.mediumScreen * 2}px
    );
    min-height: calc(
      100svh - ${HEADER_HEIGHT + SEPARATOR_HEIGHT.mediumScreen * 2}px
    );
    max-height: calc(
      100svh - ${HEADER_HEIGHT + SEPARATOR_HEIGHT.mediumScreen * 2}px
    );
  }
`;

/* ✅ התמונה של ה־Hero כסוג של “שכבת רקע” אמיתית */
const HeroBgImg = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  background: #000;
  z-index: 0;
`;

/* תוכן מעל התמונה */
const SectionContent = styled.div`
  position: relative;
  z-index: 1;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
`;

interface SectionProps {
  children?: React.ReactNode;
  sectionIndex: number;
  backgroundImageUrl?: string;
  /** מומלץ: alt של ה־Hero (רק לסקשן 0). לשאר מתעלמים */
  heroAlt?: string;
  heroImageSrcSet?: string;
  heroImageSizes?: string;
  shouldLoad?: boolean;
  onHeroReady?: () => void;
}

const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      children,
      sectionIndex,
      backgroundImageUrl,
      heroAlt,
      heroImageSrcSet,
      heroImageSizes = '100vw',
      shouldLoad = false,
      onHeroReady,
    },
    ref,
  ) => {
    const localRef = useRef<HTMLElement | null>(null);
    const heroImageRef = useRef<HTMLImageElement | null>(null);
    const previousHeroMediaKeyRef = useRef<string | null>(null);
    const didNotifyHeroReady = useRef(false);
    const isHero = sectionIndex === 0;
    const [shouldLoadBackground, setShouldLoadBackground] = useState(isHero);
    const heroMediaKey = isHero
      ? [backgroundImageUrl ?? '', heroImageSrcSet ?? '', heroImageSizes].join(
          '|',
        )
      : null;

    if (
      isHero &&
      heroMediaKey !== null &&
      previousHeroMediaKeyRef.current !== heroMediaKey
    ) {
      previousHeroMediaKeyRef.current = heroMediaKey;
      didNotifyHeroReady.current = false;
    }

    const setRef = (el: HTMLElement | null) => {
      localRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref)
        (ref as React.MutableRefObject<HTMLElement | null>).current = el;
    };

    const notifyHeroReady = useCallback(() => {
      if (!isHero || didNotifyHeroReady.current) return;
      didNotifyHeroReady.current = true;
      onHeroReady?.();
    }, [isHero, onHeroReady]);

    const setHeroImageRef = useCallback(
      (img: HTMLImageElement | null) => {
        heroImageRef.current = img;
        if (!img || !img.complete) return;

        notifyHeroReady();
      },
      [notifyHeroReady],
    );

    useEffect(() => {
      if (!isHero || backgroundImageUrl) return;
      notifyHeroReady();
    }, [backgroundImageUrl, isHero, notifyHeroReady]);

    useEffect(() => {
      if (!isHero || !backgroundImageUrl) return;

      const img = heroImageRef.current;
      if (!img || !img.complete) return;

      notifyHeroReady();
    }, [backgroundImageUrl, heroMediaKey, isHero, notifyHeroReady]);

    useEffect(() => {
      if (isHero) {
        setShouldLoadBackground(true);
        return;
      }

      if (!shouldLoad || shouldLoadBackground) return;

      setShouldLoadBackground(true);
    }, [isHero, shouldLoad, shouldLoadBackground]);

    const shouldRenderHeroImg = isHero && Boolean(backgroundImageUrl);
    const useCssBg = !isHero && shouldLoad && shouldLoadBackground;
    const shouldRenderContent = isHero || shouldLoad;

    return (
      <SectionBase
        ref={setRef}
        $useCssBg={useCssBg}
        $backgroundImageUrl={backgroundImageUrl}
      >
        {/* ✅ Hero: IMG אמיתי עם עדיפות גבוהה */}
        {shouldRenderHeroImg && (
          <HeroBgImg
            key={heroMediaKey}
            ref={setHeroImageRef}
            src={backgroundImageUrl}
            alt={heroAlt ?? HERO_IMAGE_ALT}
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            width={1920}
            height={1080}
            srcSet={heroImageSrcSet}
            sizes={heroImageSizes}
            onLoad={notifyHeroReady}
            onError={notifyHeroReady}
          />
        )}

        <SectionContent>{shouldRenderContent ? children : null}</SectionContent>
      </SectionBase>
    );
  },
);

Section.displayName = 'Section';
export default Section;
